// Shared classification and formatting helpers for the SQL Server Maintenance page and its run components.
// The string constants mirror Controllers/DbMaintenanceDtos.cs (DbMaintenanceRunKind, DbMaintenanceRunStatus,
// DbMaintenanceStepStatus and the cutover server/phase names).

/** Run statuses during which the orchestrator is still working (or waiting for the operator) and the run lock is held. */
export const activeRunStatuses = ['Running', 'WaitingForOperator', 'ManualHold'];

/** Run statuses that demand the administrator's attention until acknowledged or recovered. */
export const attentionRunStatuses = ['NeedsAttention', 'Interrupted'];

/** Kinds the administrator may start from the page, in display order. */
export const startableRunKinds = ['MigrateAndCutover', 'MigrateOnly', 'CutoverOnly', 'RollbackLastCutover', 'ResumeMigration', 'TakeLocalBaseBackups'];

export function isActiveRunStatus(status)
{
	return activeRunStatuses.indexOf(status) !== -1;
}
export function isTerminalRunStatus(status)
{
	return !!status && !isActiveRunStatus(status);
}
export function isAttentionRunStatus(status)
{
	return attentionRunStatuses.indexOf(status) !== -1;
}
/**
 * True when a server error string is the later-phase stub's "Not available yet: ..." reply.  The page shows these as a
 * grey "feature not available in this build" note instead of a red failure.
 */
export function isNotAvailableYet(error)
{
	return typeof error === 'string' && /^\s*not available yet\b/i.test(error);
}
export function runKindLabel(kind)
{
	switch (kind)
	{
		case 'MigrateAndCutover': return 'Migrate + cut over';
		case 'MigrateOnly': return 'Migrate only (no web-app cutover)';
		case 'CutoverOnly': return 'Cut over only (no migration)';
		case 'RollbackLastCutover': return 'Roll back the last web-app cutover';
		case 'ResumeMigration': return 'Resume interrupted migration';
		case 'TakeLocalBaseBackups': return 'Take local base backups';
		case 'AutomatedWindow': return 'Automated maintenance window';
		case 'CutoverRecovery': return 'Cutover recovery (startup convergence)';
		default: return kind || 'Unknown operation';
	}
}
export function runStatusLabel(status)
{
	switch (status)
	{
		case 'WaitingForOperator': return 'Waiting for operator';
		case 'ManualHold': return 'Manual hold';
		case 'SucceededWithWarnings': return 'Succeeded with warnings';
		case 'NeedsAttention': return 'NEEDS ATTENTION';
		default: return status || 'Unknown';
	}
}
export function runStatusBadgeClass(status)
{
	switch (status)
	{
		case 'Running':
			return 'badgeInfo';
		case 'WaitingForOperator':
		case 'ManualHold':
			return 'badgeHold';
		case 'Succeeded':
			return 'badgeGood';
		case 'SucceededWithWarnings':
			return 'badgeWarn';
		case 'Failed':
		case 'Interrupted':
		case 'NeedsAttention':
			return 'badgeBad';
		default:
			return 'badgeOff';
	}
}
export function stepStatusIcon(status)
{
	switch (status)
	{
		case 'Succeeded': return '✓';
		case 'Warning': return '!';
		case 'Failed': return '✗';
		case 'Running': return '▶';
		case 'Skipped': return '–';
		case 'Cancelled': return '⊘';
		default: return '·';
	}
}
export function stepStatusClass(status)
{
	switch (status)
	{
		case 'Succeeded': return 'stepSucceeded';
		case 'Warning': return 'stepWarning';
		case 'Failed': return 'stepFailed';
		case 'Running': return 'stepRunning';
		case 'Skipped': return 'stepSkipped';
		case 'Cancelled': return 'stepCancelled';
		default: return 'stepPending';
	}
}
/** Badge class for a cutover server state: Pending | Armed | Sent | OnTarget | OnSource | Split | Unreachable | Failed. */
export function serverStateBadgeClass(state)
{
	switch (state)
	{
		case 'OnTarget': return 'badgeGood';
		case 'OnSource': return 'badgeOff';
		case 'Armed':
		case 'Sent': return 'badgeInfo';
		case 'Pending': return 'badgeOff';
		case 'Split':
		case 'Unreachable':
		case 'Failed':
			return 'badgeBad';
		default: return 'badgeOff';
	}
}
/** Badge class for a cutover plan phase: Planned | Arming | Armed | Committing | Verifying | Completed | RollingBack | RolledBack | Abandoned | NeedsAttention. */
export function cutoverPhaseBadgeClass(phase)
{
	switch (phase)
	{
		case 'Completed': return 'badgeGood';
		case 'Arming':
		case 'Armed':
		case 'Committing':
		case 'Verifying':
			return 'badgeInfo';
		case 'RollingBack':
		case 'RolledBack':
		case 'Abandoned':
			return 'badgeWarn';
		case 'NeedsAttention': return 'badgeBad';
		default: return 'badgeOff';
	}
}
/** Short time for recent timestamps, full date and time once more than 12 hours away from now. */
export function formatUtc(value)
{
	if (!value)
		return '—';
	let d = new Date(value);
	if (isNaN(d.getTime()))
		return String(value);
	return Math.abs(Date.now() - d.getTime()) > 12 * 60 * 60 * 1000 ? d.toLocaleString() : d.toLocaleTimeString();
}
/** Always the full local date and time. */
export function formatDateTime(value)
{
	if (!value)
		return '—';
	let d = new Date(value);
	return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}
export function formatElapsed(ms)
{
	if (ms === null || typeof ms === 'undefined' || isNaN(ms))
		return '?';
	let s = Math.floor(Math.max(0, ms) / 1000);
	if (s < 60)
		return s + ' s';
	let m = Math.floor(s / 60);
	if (m < 60)
		return m + ' min ' + (s % 60) + ' s';
	return Math.floor(m / 60) + ' h ' + (m % 60) + ' min';
}
export function formatSeconds(seconds, digits)
{
	if (seconds === null || typeof seconds === 'undefined' || isNaN(seconds))
		return '—';
	let n = Number(seconds);
	return (typeof digits === 'number' ? n.toFixed(digits) : (n >= 100 ? n.toFixed(0) : n.toFixed(1))) + ' s';
}
export function formatMs(ms)
{
	if (ms === null || typeof ms === 'undefined' || isNaN(ms))
		return '—';
	return Number(ms).toLocaleString() + ' ms';
}
/**
 * Describes an absolute UTC deadline relative to now.
 * @returns {{ text: String, passed: Boolean, soon: Boolean }} or null when there is no deadline.
 */
export function formatCountdown(deadlineUtc, nowMs)
{
	if (!deadlineUtc)
		return null;
	let d = new Date(deadlineUtc);
	if (isNaN(d.getTime()))
		return { text: String(deadlineUtc), passed: false, soon: false };
	let remaining = d.getTime() - (nowMs || Date.now());
	if (remaining <= 0)
		return { text: 'passed ' + formatElapsed(-remaining) + ' ago (' + d.toLocaleTimeString() + ')', passed: true, soon: false };
	return { text: 'in ' + formatElapsed(remaining) + ' (' + d.toLocaleTimeString() + ')', passed: false, soon: remaining < 5 * 60 * 1000 };
}
export function joinNames(arr)
{
	return Array.isArray(arr) && arr.length ? arr.join(', ') : '';
}
